/**
 * useTypingStore — Global Zustand store for typing indicators
 *
 * Tracks which users are typing in which conversations.
 * Used by both ChatPage (updates the store) and conversation lists (reads from it).
 */

import { create } from 'zustand';

interface TypingUser {
  userId: string;
  userName: string;
  userAvatar?: string;
}

interface TypingState {
  /** Map of conversationId → currently typing users */
  typing: Record<string, TypingUser[]>;
  /** Add a typing user to a conversation (with auto-clear timeout) */
  setTyping: (conversationId: string, user: TypingUser) => void;
  /** Remove a typing user from a conversation */
  clearTyping: (conversationId: string, userId: string) => void;
  /** Get typing users for a conversation */
  getTyping: (conversationId: string) => TypingUser[];
}

const TYPING_TIMEOUT = 5000;
const timeouts = new Map<string, ReturnType<typeof setTimeout>>();

export const useTypingStore = create<TypingState>((set, get) => ({
  typing: {},

  setTyping: (conversationId, user) => {
    const key = `${conversationId}:${user.userId}`;

    // Clear existing timeout for this user
    const existing = timeouts.get(key);
    if (existing) clearTimeout(existing);

    // Set auto-clear timeout
    timeouts.set(
      key,
      setTimeout(() => {
        get().clearTyping(conversationId, user.userId);
        timeouts.delete(key);
      }, TYPING_TIMEOUT)
    );

    set((state) => {
      const current = state.typing[conversationId] || [];
      if (current.some((u) => u.userId === user.userId)) return state;
      return {
        typing: {
          ...state.typing,
          [conversationId]: [...current, user],
        },
      };
    });
  },

  clearTyping: (conversationId, userId) => {
    const key = `${conversationId}:${userId}`;
    const existing = timeouts.get(key);
    if (existing) {
      clearTimeout(existing);
      timeouts.delete(key);
    }

    set((state) => {
      const current = state.typing[conversationId];
      if (!current) return state;
      const filtered = current.filter((u) => u.userId !== userId);
      if (filtered.length === current.length) return state;
      const newTyping = { ...state.typing };
      if (filtered.length === 0) {
        delete newTyping[conversationId];
      } else {
        newTyping[conversationId] = filtered;
      }
      return { typing: newTyping };
    });
  },

  getTyping: (conversationId) => {
    return get().typing[conversationId] || [];
  },
}));

const EMPTY_TYPING: TypingUser[] = [];

/** Selector hook for a specific conversation's typing users */
export function useConversationTyping(conversationId: string | null): TypingUser[] {
  return useTypingStore((s) => (conversationId ? s.typing[conversationId] ?? EMPTY_TYPING : EMPTY_TYPING));
}
